import type { Placement } from "@floating-ui/react";
import type React from "react";
import { twJoin } from "tailwind-merge";

import type { useInputTooltip } from "./useInputTooltip";

export function InputTooltip({
  tooltip,
  children,
}: {
  children: React.ReactNode;
  tooltip: ReturnType<typeof useInputTooltip>;
}): React.ReactNode {
  return (
    tooltip.isMounted && (
      <div
        ref={tooltip.refs.setFloating}
        style={tooltip.floatingStyles}
        role="tooltip"
        className="z-50"
        {...tooltip.getFloatingProps()}
      >
        <div
          data-state={tooltip.isOpen ? "open" : "closed"}
          data-placement={getPlacement(tooltip.placement)}
          style={{
            animationFillMode: "forwards",
          }}
          className={twJoin(
            "whitespace-pre-line rounded-sm bg-grey-900 px-3 py-2 text-caption text-white shadow-md",
            "data-[placement=bottom]:origin-top data-[placement=left]:origin-right data-[placement=right]:origin-left data-[placement=top]:origin-bottom",
            "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out",
          )}
        >
          <InputTooltipArrow
            isTop={tooltip.placement.startsWith("top")}
            isBottom={tooltip.placement.startsWith("bottom")}
          />
          {children}
        </div>
      </div>
    )
  );
}

function InputTooltipArrow({ isTop, isBottom }: { isTop?: boolean; isBottom?: boolean }) {
  return (
    <div
      className="absolute inset-x-0 mx-auto size-2 rotate-45 bg-grey-900"
      style={{
        top: isBottom ? -4 : undefined,
        bottom: isTop ? -4 : undefined,
      }}
      aria-hidden
    />
  );
}

function getPlacement(placement: Placement): string {
  if (placement.startsWith("bottom")) {
    return "bottom";
  }

  if (placement.startsWith("left")) {
    return "left";
  }

  if (placement.startsWith("right")) {
    return "right";
  }

  return "top";
}
